import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { FormControl, FormGroup, Validators, NgForm, UntypedFormControl } from '@angular/forms';
import { without, keyBy } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { NoEmptyString } from '../../interfaces/no-empty-string.validator';
import { IPageResult } from '../../interfaces/page-result';
import {
  ICreateTermsConditionsVersion,
  ITermsConditionsAudienceType,
  ITermsConditionsVersion,
} from '../../interfaces/terms-conditions-version';
import { ITermsConditionsVersionFilter } from '../../interfaces/terms-conditions-version-filter';
import { TermsConditionsService } from '../../services';
import { SelectItem, LazyLoadEvent } from 'primeng';
import { MessageService } from 'primeng';
import { Message } from '../../classes/Message';

@Component({
  selector: 'terms-conditions-list',
  templateUrl: 'terms-conditions-list.component.html',
})
export class TermsAndConditionsListComponent implements OnInit {
  public ctlText: FormControl;
  public ctlVersion: FormControl;
  public ctlOrganisationTypeId: FormControl;
  public ctlAudienceTypeId: FormControl;
  public ctlType: FormControl;
  public ctlIsActual: FormControl;
  public editTermsForm: FormGroup;

  selectedTerms: ITermsConditionsVersion;

  editDialogVisible: boolean;

  private _orgTypes = {
    1: 'Engineius',
    2: 'Customer',
    3: 'Supplier',
  };

  orgTypeOptions: SelectItem[] = [{ label: 'Please Select', value: null }].concat(
    Object.keys(this._orgTypes).map((k) => ({ label: this._orgTypes[k], value: Number(k) })),
  );

  private _termsAndConditionsTypes = {
    0: 'Agreement',
    1: 'Terms of Use',
  };

  termsAndConditionsTypeOptions: SelectItem[] = [{ label: 'Please Select', value: null }].concat(
    Object.keys(this._termsAndConditionsTypes).map((k) => ({
      label: this._termsAndConditionsTypes[k],
      value: Number(k),
    })),
  );

  audienceTypes: SelectItem[] = [];

  private _audienceTypes: ITermsConditionsAudienceType[] = [];

  getAudienceTypeLabel(audienceTypeId: number) {
    let audtype = this._audienceTypes.find((x) => x.id == audienceTypeId);
    return audtype ? audtype.name : '';
  }

  public page: IPageResult<ITermsConditionsVersion> = {
    items: [],
    total: 0,
  };

  computeWarnings(): string[] {
    let warnings: string[] = [];
    if (!this.isLoaded) {
      return warnings;
    }
    // Check for active version across Customer & Suppliers for all T&Cs types
    for (let type in this._termsAndConditionsTypes) {
      let activeVersionsForType = this.page.items.filter(
        (x) => x.type.toString() == type && x.isActual,
      );

      // Remove Engineius org type from Agreement warnings
      let requiredOrgTypes =
        Number(type) == 0 ? without(Object.keys(this._orgTypes), '1') : Object.keys(this._orgTypes);

      let missingOrgTypes = without(
        requiredOrgTypes,
        ...activeVersionsForType.map((x) => x.organisationTypeId.toString()),
      );

      missingOrgTypes.forEach((x) => {
        warnings.push(
          `No active ${this._termsAndConditionsTypes[type]} exists for the ${this._orgTypes[x]} organisation type.`,
        );
      });

      // If a specific audience is specified for particular org type then all audience options should be covered
      for (let orgType in this._orgTypes) {
        let specificAudienceVersions = activeVersionsForType.filter(
          (x) => x.organisationTypeId.toString() == orgType && x.audienceTypeId != null,
        );
        if (specificAudienceVersions.length > 0) {
          let missingAudienceTypes = without(
            this._audienceTypes
              .filter((t) => t.organisationTypeId.toString() == orgType)
              .map((x) => x.id),
            ...specificAudienceVersions.map((x) => x.audienceTypeId),
          );
          missingAudienceTypes.forEach((x) => {
            warnings.push(
              `No active ${this._termsAndConditionsTypes[type]} exists for the ${
                keyBy(this._audienceTypes, 'id')[x].name
              } audience (${this._orgTypes[orgType]})`,
            );
          });
        }
      }
    }
    return warnings;
  }

  warnings: string[] = [];

  public isLoaded: boolean = false;
  public filterVal: ITermsConditionsVersionFilter;
  public filter: Subject<ITermsConditionsVersionFilter>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private tcService: TermsConditionsService,
    public messageService: MessageService,
  ) {
    // autorun(() => {
    //     this.warnings = this.computedWarnings
    // })
  }

  ngOnInit() {
    this.initDefaultFilter();
    this.filter = new Subject<ITermsConditionsVersionFilter>();

    // subscribe to filter changes with a debounce time
    this.filter.debounceTime(500).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });

    // set default filter
    this.filter.next(this.filterVal);

    //--- set the dialog form
    this.ctlText = new FormControl(
      '',
      Validators.compose([Validators.required, Validators.maxLength(100000), NoEmptyString]),
    );
    this.ctlVersion = new FormControl(
      '',
      Validators.compose([Validators.required, Validators.maxLength(20), NoEmptyString]),
    );
    this.ctlOrganisationTypeId = new FormControl(null, Validators.required);
    this.ctlAudienceTypeId = new FormControl(null);
    this.ctlIsActual = new FormControl(false);
    this.ctlType = new FormControl(null, Validators.required);
    this.editTermsForm = new FormGroup({
      id: new FormControl(0),
      text: this.ctlText,
      version: this.ctlVersion,
      organisationTypeId: this.ctlOrganisationTypeId,
      audienceTypeId: this.ctlAudienceTypeId,
      isActual: this.ctlIsActual,
      type: this.ctlType,
      htmlUrl: new FormControl(''),
      requiredByDate: new FormControl('', (validator: UntypedFormControl) => {
        if (!validator.value) {
          validator.markAsDirty();
          return { valid: false, error: 'The "Required By Date" field cannot be empty' };
        }
      }),
    });

    this.tcService.getAudienceTypes().then((res) => {
      this._audienceTypes = res;
    });

    this.ctlOrganisationTypeId.valueChanges.subscribe((value) => {
      this.ctlAudienceTypeId.setValue(null);
      this.audienceTypes = [{ label: 'None', value: null }].concat(
        this._audienceTypes
          .filter((x) => x.organisationTypeId == value)
          .map((x) => ({ label: x.name, value: x.id })),
      );
    });
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'Version',
      sortOrder: 1,
      filters: {},
    };
  }

  load(filter: ITermsConditionsVersionFilter) {
    this.tcService.getPage(filter).then((result) => {
      this.selectedTerms = null;
      this.page.items = result.items;
      this.page.total = result.total;
      this.isLoaded = true;
      this.warnings = this.computeWarnings();
    });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    // map grid lazy load event to filter
    this.filter.next(this.filterVal);
  }

  showEditDialog(terms: ITermsConditionsVersion) {
    //if new record
    if (terms == null) {
      this.editTermsForm.setValue({
        id: 0,
        isActual: false,
        version: '',
        text: '',
        organisationTypeId: null,
        audienceTypeId: null,
        type: null,
        htmlUrl: '',
        requiredByDate: null,
      });
      this.editTermsForm.clearValidators();
    } else {
      this.editTermsForm.setValue({
        id: terms.id,
        isActual: terms.isActual,
        version: terms.version,
        text: terms.text,
        organisationTypeId: terms.organisationTypeId,
        audienceTypeId: terms.audienceTypeId,
        type: terms.type,
        htmlUrl: terms.htmlUrl,
        requiredByDate: terms.requiredByDate ? new Date(terms.requiredByDate) : null,
      });
    }

    this.editDialogVisible = true;
  }

  saveTerms(formValues) {
    let tc: ICreateTermsConditionsVersion = {
      id: formValues.id,
      isActual: formValues.isActual,
      organisationTypeId: formValues.organisationTypeId,
      audienceTypeId: formValues.audienceTypeId,
      text: formValues.text,
      version: formValues.version,
      type: formValues.type,
      documentUrl: null,
      htmlUrl: formValues.htmlUrl,
    };

    tc.requiredByDate = formValues.requiredByDate
      ? new Date(formValues.requiredByDate).toDateString()
      : null;

    this.tcService.saveVersion(tc, tc.id == 0).then(() => {
      this.load(this.filterVal);
    });

    this.editDialogVisible = false;
  }

  downloadPdf(path) {
    this.tcService.downloadPdf(path);
  }

  onBeforeUpload(tcvId, event: { xhr: XMLHttpRequest; formData: FormData }): void {
    event.formData.append('tcvId', JSON.stringify(tcvId));
  }

  onUpload(event) {
    let response = event.originalEvent.body;
    if (response.successfully) {
      this.load(this.filterVal);
      this.messageService.add(Message.Success('PDF successfully uploaded', 'File Uploading'));
    } else {
      this.messageService.add(Message.Error(response.errorMessage, 'File Uploading'));
    }
  }

  onUploadError(event) {
    this.messageService.add(
      Message.Error('An error occurred while uploading files. Please try again.', 'File Uploading'),
    );
  }
}
